import { ref, type Ref } from 'vue'

/**
 * Hands the FAIR wordmark between the hero and the site header (FE-002).
 *
 * In the mock the header's `logo` instance is `hidden` on the hero screen
 * (`11:42064`) and visible on every section below it (`11:42096` and friends) —
 * the wordmark belongs to the hero until the first scroll, then it belongs to
 * the header. The transition between those two states is this flight.
 *
 * One animation owns the whole trajectory: a fixed clone travels the straight
 * line between the two slots, start to finish, on a single easing. What changes
 * halfway is only *which element is showing it*.
 *
 * That split exists because of depth. In the hero the wordmark sits behind the
 * rail (z1 under the runtime's z2) — the overlap is the whole point of FE-001 —
 * while a clone that has to land inside the header must be above the page. An
 * element cannot be on both sides of the rack, and no amount of z-index moves a
 * page-level node in between two children of the hero.
 *
 * So while the letters are still low enough to touch the rack, the hero's own
 * wordmark is the one on screen, pinned frame by frame onto the flight path;
 * the clone is running all along, just not shown yet. The moment the path lifts
 * the letters clear of the rack the two swap. They are on the same rect at the
 * same size at that instant, and nothing overlaps anymore, so neither the swap
 * nor the change of layer can be seen — the motion itself never pauses.
 *
 * Measuring beats hardcoding throughout — the hero wordmark is sized in
 * `--fair-hero-u` (viewport-derived), the header slot changes width between
 * desktop and compact, and the hero rect moves while the pager animates.
 */
const FLIGHT_EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)'

/** px of slack when deciding the letters are clear of the rack */
const CLEAR_EPSILON = 1

const HERO_SELECTOR = '.fair-home-v2-hero'
const HERO_WORDMARK_SELECTOR = '.fair-home-v2-hero__wordmark'
const HERO_WORDMARK_IMG_SELECTOR = '.fair-home-v2-hero__wordmark-img'
/**
 * The rail *host* is `inset: 0` — its box is the whole hero and says nothing
 * about where the rack starts. The runtime's stage is the drawn scene, and its
 * top edge is the topmost pixel of the rack.
 */
const HERO_RAIL_SELECTOR = '.fair-home-v2-hero__rail .fr-stage'
const HEADER_LOGO_SELECTOR = '.fair-home-v2__header .fair-site-header__logo-img'

export type FairHomeV2LogoSlot = 'hero' | 'header'

export function useFairHomeV2LogoFlight(
  rootRef: Ref<HTMLElement | null>,
  durationMs: number,
) {
  /** the hero owns the wordmark on first paint, so the header starts blank */
  const headerLogoHidden = ref(true)
  const heroWordmarkHidden = ref(false)
  const flightRef = ref<HTMLImageElement | null>(null)
  const flightVisible = ref(false)

  let running: Animation | null = null
  let frame = 0

  function query(selector: string): HTMLElement | null {
    return rootRef.value?.querySelector<HTMLElement>(selector) ?? null
  }

  function releaseWordmark() {
    const wordmark = query(HERO_WORDMARK_SELECTOR)
    if (!wordmark) return
    wordmark.style.transform = ''
    wordmark.style.transformOrigin = ''
  }

  function cancelMotion() {
    running?.cancel()
    running = null
    if (frame) cancelAnimationFrame(frame)
    frame = 0
    releaseWordmark()
  }

  /** Put both slots in their resting state for `slot`, with nothing in flight. */
  function settle(slot: FairHomeV2LogoSlot) {
    cancelMotion()
    flightVisible.value = false
    headerLogoHidden.value = slot === 'hero'
    heroWordmarkHidden.value = slot === 'header'
  }

  function play(slot: FairHomeV2LogoSlot) {
    const clone = flightRef.value
    const hero = query(HERO_SELECTOR)
    const wordmark = query(HERO_WORDMARK_SELECTOR)
    const image = query(HERO_WORDMARK_IMG_SELECTOR)
    const header = query(HEADER_LOGO_SELECTOR)

    // Everything has to be laid out to be measured. The header logo is only
    // transparent, never `display: none`, so it always reports a box; the hero
    // is gone as soon as its slide unmounts — then there is nothing to fly and
    // the slots just swap.
    if (!clone || !hero || !wordmark || !image || !header) {
      settle(slot)
      return
    }

    cancelMotion()
    headerLogoHidden.value = true

    const headerRect = header.getBoundingClientRect()
    const live = image.getBoundingClientRect()
    // The hero may be mid-transition, so its wordmark is wherever the slide has
    // carried it. Shifting it back by the hero's own offset gives the rect it
    // settles into — where the flight has to end when coming back.
    const heroTop = hero.getBoundingClientRect().top
    const heroRect = new DOMRect(live.left, live.top - heroTop, live.width, live.height)

    const from = slot === 'header' ? live : headerRect
    const to = slot === 'header' ? headerRect : heroRect
    if (!from.width || !to.width) {
      settle(slot)
      return
    }

    clone.style.width = `${from.width}px`
    clone.style.height = `${from.height}px`

    const animation = clone.animate(
      [
        { transform: `translate(${from.left}px, ${from.top}px) scale(1)` },
        {
          transform: `translate(${to.left}px, ${to.top}px) scale(${to.width / from.width})`,
        },
      ],
      { duration: durationMs, easing: FLIGHT_EASE, fill: 'forwards' },
    )
    running = animation

    // Outbound the hero carries the letters first, inbound it takes them back
    // at the end — either way the clone shows only while they are clear of the
    // rack.
    flightVisible.value = slot === 'hero'
    heroWordmarkHidden.value = slot === 'hero'

    const rail = query(HERO_RAIL_SELECTOR)
    if (rail) {
      // Where the wordmark would sit with no transform on it. The slide keeps
      // moving underneath, so this is re-derived every frame from the hero's
      // own offset rather than captured once.
      const baseTop = live.top - heroTop
      const follow = () => {
        frame = 0
        if (running !== animation) return

        const flying = clone.getBoundingClientRect()
        const clear = flying.bottom <= rail.getBoundingClientRect().top + CLEAR_EPSILON

        if (clear !== flightVisible.value) {
          // The swap: same rect, same size, nothing overlapping. Only the layer
          // the letters are drawn on changes.
          flightVisible.value = clear
          heroWordmarkHidden.value = clear
          if (clear) {
            releaseWordmark()
            return
          }
        }

        if (!clear) {
          const base = baseTop + hero.getBoundingClientRect().top
          wordmark.style.transformOrigin = '0 0'
          wordmark.style.transform = `translate(${flying.left - live.left}px, ${flying.top - base}px)`
            + ` scale(${flying.width / live.width})`
        }
        frame = requestAnimationFrame(follow)
      }
      follow()
    }

    animation.finished
      .then(() => {
        // A newer flight may have started and already settled the slots.
        if (running === animation) settle(slot)
      })
      .catch(() => {
        /* cancelled by a newer flight — that one owns the final state */
      })
  }

  return {
    headerLogoHidden,
    heroWordmarkHidden,
    flightRef,
    flightVisible,
    play,
    settle,
    stop: cancelMotion,
  }
}
